/**
 * 五行能量计算引擎（共享模块）
 * 为生肖年运和八字流年提供统一的五行分布计算
 */

import { type FourPillars, STEM_ELEMENT, BRANCH_HIDDEN as BAZI_BRANCH_HIDDEN } from '@/utils/baziCalc';

// ========== 基础命理常量 ==========

export const STEM_WUXING: Record<string, string> = {
  '甲': '木', '乙': '木', '丙': '火', '丁': '火',
  '戊': '土', '己': '土', '庚': '金', '辛': '金',
  '壬': '水', '癸': '水',
};

export const BRANCH_WUXING: Record<string, string> = {
  '子': '水', '丑': '土', '寅': '木', '卯': '木',
  '辰': '土', '巳': '火', '午': '火', '未': '土',
  '申': '金', '酉': '金', '戌': '土', '亥': '水',
};

/** 地支藏干（简化版，无权重） */
export const BRANCH_HIDDEN_SIMPLIFIED: Record<string, string[]> = {
  '子': ['癸'], '丑': ['己', '癸', '辛'], '寅': ['甲', '丙', '戊'],
  '卯': ['乙'], '辰': ['戊', '乙', '癸'], '巳': ['丙', '庚', '戊'],
  '午': ['丁', '己'], '未': ['己', '丁', '乙'], '申': ['庚', '壬', '戊'],
  '酉': ['辛'], '戌': ['戊', '辛', '丁'], '亥': ['壬', '甲'],
};

/** 地支六冲 */
export const CHONG_PAIRS: [string, string][] = [
  ['子', '午'], ['丑', '未'], ['寅', '申'],
  ['卯', '酉'], ['辰', '戌'], ['巳', '亥'],
];

/** 地支六合 */
export const LIUHE_PAIRS: [string, string][] = [
  ['子', '丑'], ['寅', '亥'], ['卯', '戌'],
  ['辰', '酉'], ['巳', '申'], ['午', '未'],
];

/** 地支三合 */
export const SANHE_GROUPS = [
  ['寅', '午', '戌'], ['亥', '卯', '未'],
  ['申', '子', '辰'], ['巳', '酉', '丑'],
];

/** 纳音五行对应表（简化版，60甲子取关键） */
export const NAYIN_MAP: Record<string, string> = {
  '庚子': '壁上土', '辛丑': '壁上土', '壬寅': '金箔金', '癸卯': '金箔金',
  '甲辰': '覆灯火', '乙巳': '覆灯火', '丙午': '天河水', '丁未': '天河水',
  '戊申': '大驿土', '己酉': '大驿土', '庚戌': '钗钏金', '辛亥': '钗钏金',
  '壬子': '桑柘木', '癸丑': '桑柘木', '甲寅': '大溪水', '乙卯': '大溪水',
  '丙辰': '沙中土', '丁巳': '沙中土', '戊午': '天上火', '己未': '天上火',
  '庚申': '石榴木', '辛酉': '石榴木', '壬戌': '大海水', '癸亥': '大海水',
  '甲子': '海中金', '乙丑': '海中金', '丙寅': '炉中火', '丁卯': '炉中火',
  '戊辰': '大林木', '己巳': '大林木', '庚午': '路旁土', '辛未': '路旁土',
  '壬申': '剑锋金', '癸酉': '剑锋金', '甲戌': '山头火', '乙亥': '山头火',
  '丙子': '涧下水', '丁丑': '涧下水', '戊寅': '城头土', '己卯': '城头土',
  '庚辰': '白蜡金', '辛巳': '白蜡金', '壬午': '杨柳木', '癸未': '杨柳木',
  '甲申': '泉中水', '乙酉': '泉中水', '丙戌': '屋上土', '丁亥': '屋上土',
  '戊子': '霹雳火', '己丑': '霹雳火', '庚寅': '松柏木', '辛卯': '松柏木',
  '壬辰': '长流水', '癸巳': '长流水', '甲午': '砂中金', '乙未': '砂中金',
  '丙申': '山下火', '丁酉': '山下火', '戊戌': '平地木', '己亥': '平地木',
};

// ─── 五行生克辅助 ───────────────────────────────────────────
export const SHENG_MAP: Record<string, string> = { '木': '火', '火': '土', '土': '金', '金': '水', '水': '木' };
export const KE_MAP: Record<string, string> = { '木': '土', '土': '水', '水': '火', '火': '金', '金': '木' };
export const BE_SHENG_MAP: Record<string, string> = { '木': '水', '火': '木', '土': '火', '金': '土', '水': '金' };
export const BE_KE_MAP: Record<string, string> = { '木': '金', '火': '水', '土': '木', '金': '火', '水': '土' };

export function isSheng(from: string, to: string): boolean { return SHENG_MAP[from] === to; }
export function isKe(from: string, to: string): boolean { return KE_MAP[from] === to; }

/** 月令旺衰评分表（用于日主强弱判断） */
export const WANGXIANG_SCORE: Record<string, Record<string, number>> = {
  '木': { '寅': 3, '卯': 3, '亥': 2, '子': 2, '辰': 1, '未': 1, '戌': -1, '丑': -1, '巳': -2, '午': -2, '申': -2, '酉': -2 },
  '火': { '巳': 3, '午': 3, '寅': 2, '卯': 2, '未': 1, '辰': 1, '戌': -1, '丑': -1, '申': -2, '酉': -2, '亥': -2, '子': -2 },
  '土': { '辰': 3, '未': 3, '戌': 3, '丑': 3, '巳': 2, '午': 2, '寅': -1, '卯': -1, '亥': -1, '子': -1, '申': -2, '酉': -2 },
  '金': { '申': 3, '酉': 3, '辰': 2, '未': 2, '戌': 2, '丑': 2, '子': 1, '亥': 1, '寅': -1, '卯': -1, '巳': -2, '午': -2 },
  '水': { '亥': 3, '子': 3, '申': 2, '酉': 2, '丑': 1, '辰': 1, '戌': -1, '未': -1, '寅': -2, '卯': -2, '巳': -2, '午': -2 },
};

/** 生肖→年支 */
export const ANIMAL_BRANCH: Record<string, string> = {
  '鼠': '子', '牛': '丑', '虎': '寅', '兔': '卯',
  '龙': '辰', '蛇': '巳', '马': '午', '羊': '未',
  '猴': '申', '鸡': '酉', '狗': '戌', '猪': '亥',
};

/** 生肖五行属性 */
export const ANIMAL_WUXING: Record<string, string> = {
  '鼠': '水', '牛': '土', '虎': '木', '兔': '木',
  '龙': '土', '蛇': '火', '马': '火', '羊': '土',
  '猴': '金', '鸡': '金', '狗': '土', '猪': '水',
};

// ─── 有原局时的完整五行结构推导 ─────────────────────────────
export function calcWuxingWithOrigin(
  fourPillars: FourPillars,
  liunian: { stem: string; branch: string },
): { wuxingPower: Record<string, number>; dominantElement: string; missingElement: string; bodyStrength: string } {
  const scores: Record<string, number> = { '金': 0, '木': 0, '水': 0, '火': 0, '土': 0 };
  const pillars = [fourPillars.year, fourPillars.month, fourPillars.day, fourPillars.hour];

  // 1. 原局四柱天干（各1.0分）
  for (const p of pillars) {
    scores[STEM_ELEMENT[p.stem]] += 1.0;
  }

  // 2. 原局四柱地支藏干（月令地支 × 1.4 权重）
  for (const p of pillars) {
    const hidden = BAZI_BRANCH_HIDDEN[p.branch];
    if (!hidden) continue;
    for (const h of hidden) {
      const hEl = STEM_ELEMENT[h.stem];
      const weight = p.label === '月柱' ? h.weight * 1.4 : h.weight;
      scores[hEl] += weight;
    }
  }

  // 3. 日主强弱判定
  const dayStemEl = STEM_ELEMENT[fourPillars.day.stem];
  const monthBranch = fourPillars.month.branch;

  let supportScore = WANGXIANG_SCORE[dayStemEl]?.[monthBranch] ?? 0;

  for (const p of pillars) {
    if (p.label === '日柱') continue;
    const el = STEM_ELEMENT[p.stem];
    if (el === dayStemEl) supportScore += 1;
    else if (isSheng(el, dayStemEl)) supportScore += 0.8;
    else if (isKe(el, dayStemEl)) supportScore -= 0.5;
  }

  for (const p of pillars) {
    const hidden = BAZI_BRANCH_HIDDEN[p.branch];
    if (!hidden) continue;
    for (const h of hidden) {
      const hEl = STEM_ELEMENT[h.stem];
      if (hEl === dayStemEl) supportScore += 0.5 * h.weight;
      else if (isSheng(hEl, dayStemEl)) supportScore += 0.3 * h.weight;
      else if (isKe(hEl, dayStemEl)) supportScore -= 0.3 * h.weight;
    }
  }

  const bodyStrength = supportScore >= 3 ? 'strong' : supportScore <= 0.5 ? 'weak' : 'neutral';

  // 喜用神
  let xiElements: string[] = [];
  let jiElements: string[] = [];
  if (bodyStrength === 'strong') {
    xiElements = [BE_KE_MAP[dayStemEl], SHENG_MAP[dayStemEl], KE_MAP[dayStemEl]].filter(Boolean);
    jiElements = [dayStemEl, BE_SHENG_MAP[dayStemEl]].filter(Boolean);
  } else if (bodyStrength === 'weak') {
    xiElements = [dayStemEl, BE_SHENG_MAP[dayStemEl]].filter(Boolean);
    jiElements = [BE_KE_MAP[dayStemEl], SHENG_MAP[dayStemEl], KE_MAP[dayStemEl]].filter(Boolean);
  } else {
    xiElements = [SHENG_MAP[dayStemEl], KE_MAP[dayStemEl]].filter(Boolean);
    jiElements = [dayStemEl, BE_SHENG_MAP[dayStemEl]].filter(Boolean);
  }

  // 4. 流年天干叠加（喜用神 × 1.6，忌神 × 0.5）
  const liuStemWx = STEM_WUXING[liunian.stem] ?? '土';
  let stemMult = 1.0;
  if (xiElements.includes(liuStemWx)) stemMult = 1.6;
  else if (jiElements.includes(liuStemWx)) stemMult = 0.5;
  scores[liuStemWx] += 1.5 * stemMult;

  // 流年地支
  const liuBranchWx = BRANCH_WUXING[liunian.branch] ?? liuStemWx;
  let branchMult = 1.0;
  if (xiElements.includes(liuBranchWx)) branchMult = 1.3;
  else if (jiElements.includes(liuBranchWx)) branchMult = 0.6;
  scores[liuBranchWx] += 1.0 * branchMult;

  // 流年藏干
  const liuHidden = BAZI_BRANCH_HIDDEN[liunian.branch as any];
  if (liuHidden) {
    for (const h of liuHidden) {
      const hWx = STEM_ELEMENT[h.stem];
      let hMult = 0.5;
      if (xiElements.includes(hWx)) hMult = 0.8;
      else if (jiElements.includes(hWx)) hMult = 0.3;
      scores[hWx] += h.weight * hMult;
    }
  }

  // 5. 流年与原局地支互动（刑冲合害）
  const allBranches = [fourPillars.year.branch, fourPillars.month.branch, fourPillars.day.branch, fourPillars.hour.branch];

  for (const pb of allBranches) {
    if (CHONG_PAIRS.some(([a, b]) => (a === liunian.branch && b === pb) || (b === liunian.branch && a === pb))) {
      const pbWx = BRANCH_WUXING[pb] ?? '土';
      scores[pbWx] -= 0.5;
      scores[liuBranchWx] += 0.2;
    }
    if (LIUHE_PAIRS.some(([a, b]) => (a === liunian.branch && b === pb) || (b === liunian.branch && a === pb))) {
      const pbWx = BRANCH_WUXING[pb] ?? '土';
      scores[pbWx] += 0.4;
      scores[liuBranchWx] += 0.3;
    }
    for (const [a, b, c, el] of [['寅', '午', '戌', '火'], ['亥', '卯', '未', '木'], ['申', '子', '辰', '水'], ['巳', '酉', '丑', '金']] as [string, string, string, string][]) {
      const group = [a, b, c];
      if (group.includes(liunian.branch) && group.includes(pb)) {
        const third = group.find(g => g !== liunian.branch && g !== pb);
        if (third && allBranches.includes(third as any)) {
          scores[el] += 0.6;
        }
      }
    }
  }

  // 6. 日主强弱修正
  if (bodyStrength === 'strong') {
    const ke = KE_MAP[dayStemEl];
    const sheng = SHENG_MAP[dayStemEl];
    const beKe = BE_KE_MAP[dayStemEl];
    if (ke) scores[ke] *= 1.15;
    if (sheng) scores[sheng] *= 1.15;
    if (beKe) scores[beKe] *= 1.1;
  } else if (bodyStrength === 'weak') {
    const beSheng = BE_SHENG_MAP[dayStemEl];
    if (dayStemEl) scores[dayStemEl] *= 1.15;
    if (beSheng) scores[beSheng] *= 1.2;
  }

  // 7. 归一化到 18-90
  const values = Object.values(scores);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const normalized: Record<string, number> = {};
  if (range > 0) {
    for (const [el, val] of Object.entries(scores)) {
      normalized[el] = Math.round(18 + ((val - min) / range) * 72);
    }
  } else {
    for (const el of Object.keys(scores)) normalized[el] = 50;
  }

  const sorted = Object.entries(normalized).sort((a, b) => b[1] - a[1]);
  return {
    wuxingPower: normalized,
    dominantElement: sorted[0][0],
    missingElement: sorted[sorted.length - 1][0],
    bodyStrength,
  };
}

// ─── 无原局时的简化五行推导（不用固定基线）────────────────────
export function calcWuxingSimple(
  liunian: { stem: string; branch: string },
  animalName: string,
): { wuxingPower: Record<string, number>; dominantElement: string; missingElement: string; bodyStrength: string } {
  const scores: Record<string, number> = { '金': 0, '木': 0, '水': 0, '火': 0, '土': 0 };
  const mingzhuWx = ANIMAL_WUXING[animalName] ?? '火';
  const liuStemWx = STEM_WUXING[liunian.stem] ?? '火';
  const liuBranchWx = BRANCH_WUXING[liunian.branch] ?? liuStemWx;

  // 以生肖五行为"虚拟日主"
  scores[mingzhuWx] += 3.0;

  // 流年天干
  scores[liuStemWx] += 2.5;

  // 流年地支
  scores[liuBranchWx] += 2.0;

  // 藏干（按位置赋权：主气1.0 / 中气0.6 / 余气0.3）
  const hidden = BRANCH_HIDDEN_SIMPLIFIED[liunian.branch] || [];
  for (let i = 0; i < hidden.length; i++) {
    const wx = STEM_WUXING[hidden[i]];
    if (wx) {
      const weight = i === 0 ? 1.0 : i === 1 ? 0.6 : 0.3;
      scores[wx] += weight;
    }
  }

  // 纳音五行微调
  const nayin = NAYIN_MAP[`${liunian.stem}${liunian.branch}`];
  if (nayin) {
    if (nayin.includes('金')) scores['金'] += 0.5;
    else if (nayin.includes('木')) scores['木'] += 0.5;
    else if (nayin.includes('水')) scores['水'] += 0.5;
    else if (nayin.includes('火')) scores['火'] += 0.5;
    else if (nayin.includes('土')) scores['土'] += 0.5;
  }

  // 生克关系
  if (SHENG_MAP[liuStemWx] === mingzhuWx) scores[liuStemWx] += 0.8;
  if (KE_MAP[liuStemWx] === mingzhuWx) {
    scores[liuStemWx] += 0.6;
    scores[mingzhuWx] -= 0.3;
  }

  // 归一化到 20-85
  const values = Object.values(scores);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const normalized: Record<string, number> = {};
  if (range > 0) {
    for (const [el, val] of Object.entries(scores)) {
      normalized[el] = Math.round(20 + ((val - min) / range) * 65);
    }
  } else {
    for (const el of Object.keys(scores)) normalized[el] = 50;
  }

  const sorted = Object.entries(normalized).sort((a, b) => b[1] - a[1]);
  return {
    wuxingPower: normalized,
    dominantElement: sorted[0][0],
    missingElement: sorted[sorted.length - 1][0],
    bodyStrength: 'neutral',
  };
}

// ========== 五行失衡分析引擎 ==========

/** 主导五行过旺的「症状」与风险领域 */
export const DOMINANT_SYMPTOMS: Record<string, { excess: string; riskArea: string }> = {
  '火': { excess: '行动力与热情已经拉满，但「冷却系统」跟不上，决策容易过热', riskArea: '冲动消费、感情用事、合同签署' },
  '木': { excess: '生长与扩张的动能极强，但「聚焦能力」不足，容易发散失控', riskArea: '项目过多导致虎头蛇尾、精力分散' },
  '土': { excess: '积累与保守的惯性很大，但「灵活性」严重滞后，容易错过窗口', riskArea: '固守旧模式、拒绝新机会、反应迟缓' },
  '金': { excess: '规则与框架意识过强，但「人情弹性」不足，容易冷酷伤人', riskArea: '人际关系摩擦、合作破裂、过度理性' },
  '水': { excess: '信息敏感度与应变能力极高，但「定力」不够，容易反复犹豫', riskArea: '错失最佳时机、决策瘫痪、机会成本' },
};

/** 缺失五行的「短板」与本质缺什么 */
export const MISSING_DEFICITS: Record<string, { deficit: string; whatYouLack: string }> = {
  '木': { deficit: '续航与生长力跟不上节奏', whatYouLack: '不是机会太少，而是承接机会的持续生长能力' },
  '火': { deficit: '热情与推动力不足', whatYouLack: '不是能力不够，而是敢于争取、主动燃烧的动力' },
  '土': { deficit: '落地根基与稳定性不足', whatYouLack: '不是方向不明，而是让方向生根的执行锚点' },
  '金': { deficit: '边界感与约束机制薄弱', whatYouLack: '不是资源不足，而是防止资源流失的规则与边界' },
  '水': { deficit: '冷静判断力与复盘能力偏弱', whatYouLack: '不是不够努力，而是努力前的方向校准与努力后的复盘修正' },
};

/**
 * 构建五行失衡的现实影响与调和建议
 * 基于真实五行分值差距 + 生克关系 + 主导/缺失组合动态生成
 */
export function buildWuxingImbalanceEffect(
  dominant: string,
  missing: string,
  domScore: number,
  missScore: number,
  wuxingPower: Record<string, number>,
  liunianWx: string,
  mingzhuWx: string,
): { effect: string; advice: string } {
  const domSym = DOMINANT_SYMPTOMS[dominant];
  const missDef = MISSING_DEFICITS[missing];
  const gap = domScore - missScore;
  const isSevere = gap >= 25;

  // 基础效果：结论先行，直接点明失衡
  const baseEffect = `${dominant}旺${missing}弱，${domSym.excess}，${missDef.deficit}。`;

  // 组合级现实影响（25种组合覆盖 + 通用回退）
  let realImpact = '';
  const comboKey = `${dominant}_${missing}`;
  const COMBO_IMPACTS: Record<string, string> = {
    '火_水': '今年最怕冲动决策——财来得快去得也快，感情用事容易错失真正的长期价值。火旺水弱，热情有余冷静不足，投资与合作中务必设置冷静期。',
    '火_木': '行动力爆棚但后劲不足，开局轰轰烈烈，收尾时却发现自己缺乏持续生长的续航力。今年适合短跑冲刺，不适合马拉松式项目。',
    '火_土': '想法落地率极低，热情驱动了很多计划，但真正生根发芽的不到三成。今年最该做的不是再想一个新点子，而是把已有的一个做透。',
    '火_金': '规则意识薄弱，财务与人际边界模糊，容易因为「熟人关系」或「一时冲动」签订不利条款。合作中务必白纸黑字。',
    '木_火': '生长力极强但推动力不足，有很多好想法却缺乏「点火」的勇气。今年最需要的是把60分的方案先推出去，而不是等100分再出手。',
    '木_土': '扩张欲望很大但根基不稳，新项目层出不穷，老项目无人维护。今年最大的陷阱是「贪多嚼不烂」。',
    '木_金': '想法太多、计划太杂，但缺乏修剪与聚焦的约束机制，资源分散导致关键目标被稀释。你缺的不是创意，是做减法的魄力。',
    '木_水': '生长方向不明，虽然一直在忙碌，但「忙」不等于「有效」。复盘能力不足，导致同样的错误在不同项目中重复。',
    '土_木': '过于保守导致错失成长窗口，明明看到了趋势，却因为「等一等、稳一稳」而落后。今年最需要的是「小步快跑」，而非「一步到位」。',
    '土_火': '执行力强但缺乏热情驱动，做事像完成任务而非追求成就。这种状态短期内稳定，长期会陷入倦怠，需要找到真正点燃自己的方向。',
    '土_水': '过于保守导致流动性枯竭，明明嗅到了机会，却因为反复评估而错过最佳入场时机。今年最大的成本是「观望的机会成本」。',
    '土_金': '积累很多但转化很差，明明有很多资源，却因为缺乏规则与流程而无法变现。今年最该建立的是「标准化输出能力」。',
    '金_木': '规则与框架意识过强，反而容易削足适履。你真正缺的不是机会，而是承接机会的稳定度——让创意在规则中生长，而非被规则扼杀。',
    '金_火': '过度理性冷酷，缺乏人情温度，容易在关键时刻因为「讲理不讲情」而失去合作机会。今年要学会「先共情，再讲理」。',
    '金_水': '分析过度导致决策瘫痪，数据越多越不敢动。今年最该练的是「60分决策」——在信息不完整时也能果断出手。',
    '金_土': '产出多但根基不稳，每一次成功都建立在高消耗之上，缺乏可持续性。今年要从「冲刺模式」切换到「马拉松模式」。',
    '水_木': '信息过载导致方向感迷失，学了太多、看了太多，却不知道自己该走哪条路。今年最该做的是「断舍离」——砍掉80%的信息输入，聚焦20%的核心赛道。',
    '水_火': '过度谨慎导致错失热情驱动的机遇，总是在「再等等、再看看」中看着别人起飞。今年需要一次「 calculated risk 」来打破僵局。',
    '水_土': '信息敏感度极高，但「落地锚点」不稳，最容易在反复比较中错过最佳入场时机。今年最大的陷阱是「分析得对，但行动得晚」。',
    '水_金': '灵活有余但边界不足，容易被他人需求牵着走，自己的时间被切割成碎片。今年最该建立的是「不可侵犯的个人规则」。',
  };

  if (COMBO_IMPACTS[comboKey]) {
    realImpact = COMBO_IMPACTS[comboKey];
  } else {
    // 基于生克关系的通用回退
    if (SHENG_MAP[missing] === dominant) {
      realImpact = `${missing}生${dominant}本应是良性循环，但${missing}过弱导致${dominant}成了「无根之火」——越旺越虚，越行动越透支。今年要避免「虚假繁荣」。`;
    } else if (SHENG_MAP[dominant] === missing) {
      realImpact = `${dominant}生${missing}本应顺畅，但${missing}承接不住，导致${dominant}的能量白白流失，付出多回报少。今年最该做的是提升${missing}的承接能力。`;
    } else if (KE_MAP[dominant] === missing) {
      realImpact = `${dominant}克${missing}，主导能量直接压制了本就薄弱的${missing}，导致${missDef.whatYouLack}。今年要刻意为${missing}创造生存空间。`;
    } else if (KE_MAP[missing] === dominant) {
      realImpact = `${missing}克${dominant}，缺失的${missing}本应约束${dominant}，但约束力不足导致${dominant}更加失控。今年需要引入外部规则来补足${missing}的缺失。`;
    } else {
      realImpact = `${dominant}与${missing}之间缺乏生助通道，能量无法顺畅流转，今年容易在「想做的事」和「能做的事」之间反复拉扯。破局点在于找到${missing}的补充路径。`;
    }
  }

  // 严重程度修饰（影响文案语气，不改变核心内容）
  const severityPrefix = isSevere ? '' : '略有偏移：';

  const effect = `${severityPrefix}${baseEffect}${realImpact}`;

  // 调和建议：基于缺失五行给出具体动作
  const ADVICE_MAP: Record<string, string> = {
    '木': `最该补的是木——建立「长线思维」：任何重要计划都要求持续投入至少3个月，用时间复利对冲${dominant}过旺的即时消耗。`,
    '火': `最该补的是火——每周设定一个「主动争取」动作：主动约见一位关键人脉、主动发起一次提案，用外驱力点燃被${dominant}压制的热情。`,
    '土': `最该补的是土——把80%精力锁定在1-2个核心目标上，其余全部砍掉。用「少即是多」的聚焦策略，为${dominant}的能量找到真正的着陆点。`,
    '金': `最该补的是金——建立硬性规则：投资/合作前必须满足3个checklist条件，任何决策设置48小时冷静期，用制度代替意志力。`,
    '水': `最该补的是水——重大决策前强制「过夜原则」：所有超过一定金额或影响周期的决定，必须睡一晚再做，用延迟对抗${dominant}的冲动。`,
  };

  const advice = ADVICE_MAP[missing] ?? `建议通过补足${missing}行能量来调和${dominant}过旺带来的失衡，在生活中多接触与${missing}相关的颜色、方位与事物。`;

  return { effect, advice };
}